import { Account, ActionType, IntegrationStatus, IntegrationType } from '~/lib/api/account-linking/types';
import StageScreen, { StageScreenDisplay } from './stage-screen';
import ActionInstructionsScreens from '../components/ActionInstructionsScreens';
import { P2GAlert, P2GBox, P2GButton, P2GLink } from '@parcel2go/core-ui';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useAccountLinkingApi } from '~/lib/api/account-linking/use-account-linking-api';
import { KnowYourCustomerApplicationTimer } from './KnowYourCustomerApplicationTimer';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import useGoto from '~/hooks/use-goto';

const LazyCmsContent = lazy(() => import('~/components/content/cms-content'));

type props = {
  showContinueButton?: boolean;
  display: StageScreenDisplay;
  account: Account;
  integrationSlug: string;
  integrationType: IntegrationType;
  onDelete?: (accountId: string, nextAction: ActionType | null) => void;
  onAccountStatusChange?: (newStatus: IntegrationStatus) => void;
};

const AllowedStatuses: IntegrationStatus[] = ['Active', 'Pending', 'Rejected'];

const ExistingAccount = ({
  display,
  onDelete,
  onAccountStatusChange,
  account,
  integrationSlug,
  integrationType,
  showContinueButton
}: props) => {
  const { goTo } = useGoto();
  const { getintegrationsummary } = useAccountLinkingApi();
  const [letMeDelete, setLetMeDelete] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [accountState, setAccountState] = useState(account);

  const returnUrl = useMemo(() => {
    // If we have a returnUrl, we should go there instead of the default order process page.
    const params = new URLSearchParams(window.location.search);
    const returnUrl = params.get('returnUrl');
    if (returnUrl) {
      return returnUrl;
    }
    return null;
  }, []);

  useEffect(() => {
    onAccountStatusChange?.(accountState?.status);
  }, [accountState?.status, onAccountStatusChange]);

  const canDelete = useMemo(() => {
    if (accountState?.actions.includes('Delete') && onDelete) {
      return true;
    }
    return false;
  }, [accountState, onDelete]);

  const reloadAccount = useCallback(
    async (accountId: string, _nextAction: ActionType | null) => {
      const r = await getintegrationsummary(integrationType);
      if (r.success) {
        const updatedAccount = r.result.flatMap((i) => i.accounts).find((a) => a.id === accountId);
        if (updatedAccount) {
          setAccountState(updatedAccount);
        }
      }
    },
    [getintegrationsummary, integrationType]
  );

  const onDeleteCallback = useCallback(
    (accountId: string, nextAction: ActionType | null) => {
      // This will reload the main list
      onDelete?.(accountId, nextAction);
    },
    [onDelete]
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const secondsUntilExpiry = useMemo(() => {
    if (!accountState || AllowedStatuses.includes(accountState.status) || !accountState.expiryDateTime) {
      return null;
    }

    // Subtract 60 seconds to account for any delays
    return Math.floor((new Date(accountState.expiryDateTime).getTime() - now) / 1000) - 60;
  }, [accountState, now]);

  // Consider expired if: seconds <= 0, OR if ConfigurationRequired with no expiryDateTime (links were cleared)
  const hasExpired =
    (secondsUntilExpiry !== null && secondsUntilExpiry <= 0) ||
    (accountState?.status === 'ConfigurationRequired' && !accountState?.expiryDateTime);

  if (letMeDelete && canDelete) {
    return (
      <ActionInstructionsScreens
        action="Delete"
        integrationSlug={integrationSlug}
        integrationType={integrationType}
        onComplete={onDeleteCallback}
        accountId={accountState.id}
        wrapper={(x) => (
          <StageScreen
            display={display}
            title={hasExpired ? 'Expired Application' : 'Delete Application'}
            subTitle={
              hasExpired
                ? 'Your application has expired as the required documents were not uploaded in time.'
                : 'Are you sure you want to delete this application? This action cannot be undone.'
            }
          >
            <P2GBox>{x}</P2GBox>
            <P2GBox textAlign="center" mt={2}>
              <P2GLink onClick={() => setLetMeDelete(false)}>Cancel</P2GLink>
            </P2GBox>
            <Suspense>
              <LazyCmsContent
                contentKey={`content-${integrationType}-${integrationSlug}-deleting-screen`.toLowerCase()}
              />
            </Suspense>
          </StageScreen>
        )}
      />
    );
  }

  if (hasExpired) {
    return (
      <StageScreen
        display={display}
        title="Application Expired"
        subTitle="Your application has expired as the required documents were not uploaded in time. Please create a new application to continue."
      >
        {canDelete && (
          <P2GBox textAlign="center" mt={2}>
            <P2GButton variant="primary" onClick={() => setLetMeDelete(true)}>
              Continue
            </P2GButton>
          </P2GBox>
        )}
        <Suspense>
          <LazyCmsContent contentKey={`content-${integrationType}-${integrationSlug}-expired-screen`.toLowerCase()} />
        </Suspense>
      </StageScreen>
    );
  }

  // All Done
  if (accountState.status === 'Active') {
    return (
      <StageScreen
        display={display}
        title="Submission Complete"
        subTitle="Your submission has been completed and approved."
      >
        <P2GBox textAlign="center">
          {showContinueButton && (
            <P2GBox>
              {returnUrl ? (
                <P2GButton href={returnUrl}>Continue</P2GButton>
              ) : (
                <P2GButton onClick={() => goTo(RoutingConstants.SmartSend)}>View Sold Items</P2GButton>
              )}
            </P2GBox>
          )}
          {canDelete && (
            <P2GBox textAlign="center" mt={2}>
              <P2GLink onClick={() => setLetMeDelete(true)}>Delete Application</P2GLink>
            </P2GBox>
          )}
          <Suspense>
            <LazyCmsContent
              contentKey={`content-${integrationType}-${integrationSlug}-complete-screen`.toLowerCase()}
            />
          </Suspense>
        </P2GBox>
      </StageScreen>
    );
  }

  // Doc Upload
  if (accountState.status === 'ConfigurationRequired' && accountState.actions.includes('Configure')) {
    return (
      <ActionInstructionsScreens
        action="Configure"
        integrationSlug={integrationSlug}
        integrationType={integrationType}
        onComplete={reloadAccount}
        accountId={accountState.id}
        wrapper={(x, title, subtitle, sectionHeading, icon) => (
          <StageScreen display={display} title={title} subTitle={subtitle} sectionHeading={sectionHeading} icon={icon}>
            <P2GBox mb={2}>
              {secondsUntilExpiry !== null && (
                <P2GAlert>
                  <KnowYourCustomerApplicationTimer seconds={secondsUntilExpiry} />
                </P2GAlert>
              )}
            </P2GBox>
            <P2GBox>{x}</P2GBox>
            {canDelete && (
              <P2GBox textAlign="center" mt={2}>
                <P2GLink onClick={() => setLetMeDelete(true)}>Cancel</P2GLink>
              </P2GBox>
            )}
            <Suspense>
              <LazyCmsContent
                contentKey={`content-${integrationType}-${integrationSlug}-upload-screen`.toLowerCase()}
              />
            </Suspense>
          </StageScreen>
        )}
      />
    );
  }

  // Submitted - Awaiting verification from API
  if (accountState.status === 'Pending' && accountState.actions.includes('Verify')) {
    return (
      <ActionInstructionsScreens
        action="Verify"
        integrationSlug={integrationSlug}
        integrationType={integrationType}
        onComplete={reloadAccount}
        accountId={accountState.id}
        wrapper={(x, title, subtitle, sectionHeading, icon) => (
          <StageScreen
            display={display}
            title="Verifying Account"
            subTitle={subtitle}
            sectionHeading={sectionHeading}
            icon={icon}
          >
            <P2GBox>{x}</P2GBox>
            {canDelete && (
              <P2GBox textAlign="center" mt={2}>
                <P2GLink onClick={() => setLetMeDelete(true)}>Cancel</P2GLink>
              </P2GBox>
            )}
            <Suspense>
              <LazyCmsContent
                contentKey={`content-${integrationType}-${integrationSlug}-verifying-screen`.toLowerCase()}
              />
            </Suspense>
          </StageScreen>
        )}
      />
    );
  }

  return (
    <StageScreen display={display} title="Account" subTitle="Oops something went wrong.">
      <P2GBox textAlign="center" mt={2}>
        <P2GLink onClick={() => setLetMeDelete(true)}>Cancel</P2GLink>
      </P2GBox>
    </StageScreen>
  );
};

export default ExistingAccount;
